import { t } from "@travily/i18n";
import type Badge from "@travily/types/models/BadgesRow";
import type TripsRow from "@travily/types/models/TripsRow";
import type { TripsRowCanTrait } from "@travily/types/models/TripsRow";
import type FamiliesShow from "@travily/types/pages/FamiliesShow";
import type Family from "@travily/types/pending/models/Family";
import type { ConfirmDialogState } from "@ui/components/ConfirmDialog";
import { Heading } from "@ui/components/Heading";
import { PassportBadges } from "@/components/PassportBadges/PassportBadges";
import { ProfileFrame } from "@/components/ProfileFrame/ProfileFrame";
import { familyProfileAccount } from "@/components/ProfileFrame/profile-account";
import type { ProfileTabHrefs } from "@/components/ProfileTabs/ProfileTabs";
import { Trips } from "@/components/Trips/Trips";

type TripWithCan = TripsRow & TripsRowCanTrait;

export interface ShowViewProps {
  badges?: Badge[];
  can?: FamiliesShow["can"];
  deleteState: ConfirmDialogState;
  family: Family;
  restricted?: boolean;
  onCreateTrip?: () => void;
  onDeleteTrip: (sqid: string) => void;
  onEditTrip: (trip: TripWithCan) => void;
  onAcceptFriendship?: () => void;
  onDeclineFriendship?: () => void;
  onFriendshipAction?: () => void;
  onShareProfile?: () => void;
  onUnfriend?: () => void;
  onViewFriends?: () => void;
  /** Profile/store tab hrefs when the profile has a store. */
  tabs?: ProfileTabHrefs;
  onViewAllBadges?: () => void;
  trips: TripWithCan[];
}

export function ShowView({
  badges = [],
  can,
  deleteState,
  family,
  onAcceptFriendship,
  onCreateTrip,
  onDeclineFriendship,
  onDeleteTrip,
  onEditTrip,
  onFriendshipAction,
  onShareProfile,
  onUnfriend,
  onViewAllBadges,
  onViewFriends,
  restricted,
  tabs,
  trips,
}: ShowViewProps) {
  return (
    <ProfileFrame
      account={familyProfileAccount(family)}
      currentTab="profile"
      onAcceptFriendship={onAcceptFriendship}
      onDeclineFriendship={onDeclineFriendship}
      onFriendshipAction={onFriendshipAction}
      onShareProfile={onShareProfile}
      onUnfriend={onUnfriend}
      onViewFriends={onViewFriends}
      restricted={restricted}
      tabs={tabs}
    >
      <div className="mt-8 flex w-full flex-col gap-4 lg:mt-12 lg:gap-6">
        <Heading.Root divider="responsive">
          <Heading.Title>{t("passport.badges.title")}</Heading.Title>
        </Heading.Root>
        <PassportBadges badges={badges} totalCount={family.total_badge_count ?? 0} onViewAll={onViewAllBadges} />
      </div>
      <div className="mt-8 w-full lg:mt-12">
        <Trips
          canCreate={can?.create_trip}
          deleteState={deleteState}
          onCreateTrip={onCreateTrip}
          onDeleteTrip={onDeleteTrip}
          onEditTrip={onEditTrip}
          owner={{
            display_name: family.display_name,
            avatar: family.avatar,
            initial: family.initial,
            profile_type: "Family",
          }}
          trips={trips}
        />
      </div>
    </ProfileFrame>
  );
}
